import { Controller } from "stimulus"
import { showTarget, hideTarget } from '../../helpers/visibility'

export default class extends Controller {
  static targets = [
    "actionDropdown",
    "bulkEditButton", 
    "buttonText",
    "checkbox",
    "backdropIds",
    "backdropRow",
    "selectAllCheckbox",
    "categoryCheckbox",
    "pricingWrapper",
    "packageSelectorWrapper",
    "advancedOptionsWrapper",
    "leadTimeFields",
    "moveToCategoryWrapper",
    "deleteWarningWrapper"
  ]

  connect() {
    this.updateCheckboxes()
    if(this.hasActionDropdownTarget) {
      this.refreshActionDropdown()
    }
  }

  refreshActionDropdown() {
    if(!this.hasActionDropdownTarget) return

    const action = this.actionDropdownTarget.value
    this.hideAllWrappers()
    switch(action) {
      case "update_pricing":
        if(this.hasPricingWrapperTarget) {
          this.enableInputs(this.pricingWrapperTarget)
          showTarget(this.pricingWrapperTarget)
        }
        break;
      case "set_associated_packages":
        if(this.hasPackageSelectorWrapperTarget) {
          this.enableInputs(this.packageSelectorWrapperTarget)
          showTarget(this.packageSelectorWrapperTarget)
        }
        break;
      case "update_advanced_options":
        if(this.hasAdvancedOptionsWrapperTarget) {
          this.enableInputs(this.advancedOptionsWrapperTarget)
          showTarget(this.advancedOptionsWrapperTarget)
        }
        break;
      case "move_to_category":
        if(this.hasMoveToCategoryWrapperTarget) {
          this.enableInputs(this.moveToCategoryWrapperTarget)
          showTarget(this.moveToCategoryWrapperTarget)
        }
        break;
      case "archive_backdrops":
      case "unarchive_backdrops":
        // No additional fields needed
        break;
      case "delete_backdrops":
        if(this.hasDeleteWarningWrapperTarget) {
          this.enableInputs(this.deleteWarningWrapperTarget)
          showTarget(this.deleteWarningWrapperTarget)
        }
        break;
      default:
        console.warn(`Unhandled action: ${action}`)
    }
  }

  updateCheckboxes() {
    const checked = this.checkboxTargets.filter(checkbox => checkbox.checked)
    const unchecked = this.checkboxTargets.filter(checkbox => !checkbox.checked)

    if(checked.length > 0) {
      showTarget(this.bulkEditButtonTarget)
      // Update button text with count
      if(this.hasButtonTextTarget) {
        this.buttonTextTarget.textContent = `Edit Selected Backdrops (${checked.length})`
      }
    }
    else {
      hideTarget(this.bulkEditButtonTarget)
    }

    if(this.hasBackdropIdsTarget) {
      this.backdropIdsTarget.value = checked.map(checkbox => checkbox.value).join(",")
    }
    
    // Update select all checkbox state
    if(this.hasSelectAllCheckboxTarget) {
      if(checked.length === this.checkboxTargets.length && checked.length > 0) {
        this.selectAllCheckboxTarget.checked = true
        this.selectAllCheckboxTarget.indeterminate = false
      } else if(checked.length > 0) {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = true
      } else {
        this.selectAllCheckboxTarget.checked = false
        this.selectAllCheckboxTarget.indeterminate = false
      }
    }
    
    // Update category checkbox states
    this.updateCategoryCheckboxStates()
  }

  toggleSelectAll() {
    if(this.selectAllCheckboxTarget.checked) {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = true
      })
    }
    else {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
    }
    this.updateCheckboxes()
  }

  getSelectedBackdrops() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }

  hideAllWrappers() {
    if(this.hasPricingWrapperTarget) {
      this.disableInputs(this.pricingWrapperTarget)
      hideTarget(this.pricingWrapperTarget)
    }
    if(this.hasPackageSelectorWrapperTarget) {
      this.disableInputs(this.packageSelectorWrapperTarget)
      hideTarget(this.packageSelectorWrapperTarget)
    }
    if(this.hasAdvancedOptionsWrapperTarget) {
      this.disableInputs(this.advancedOptionsWrapperTarget)
      hideTarget(this.advancedOptionsWrapperTarget)
    }
    if(this.hasMoveToCategoryWrapperTarget) {
      this.disableInputs(this.moveToCategoryWrapperTarget)
      hideTarget(this.moveToCategoryWrapperTarget)
    }
    if(this.hasDeleteWarningWrapperTarget) {
      this.disableInputs(this.deleteWarningWrapperTarget)
      hideTarget(this.deleteWarningWrapperTarget)
    }
  }

  disableInputs(wrapper) {
    const inputs = wrapper.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      input.disabled = true
    })
  }

  enableInputs(wrapper) {
    const inputs = wrapper.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      input.disabled = false
    })
  }

  updateAdvancedFields() {
    // Show/hide lead time field based on requires lead time selection
    const leadTimeRadio = this.element.querySelector('input[name="command[requires_lead_time]"]:checked')
    if (leadTimeRadio && leadTimeRadio.value === "yes") {
      if(this.hasLeadTimeFieldsTarget) showTarget(this.leadTimeFieldsTarget)
    } else {
      if(this.hasLeadTimeFieldsTarget) hideTarget(this.leadTimeFieldsTarget)
    }
  }

  toggleCategorySelection(event) {
    const categoryCheckbox = event.target
    const categoryId = categoryCheckbox.dataset.categoryId
    const isChecked = categoryCheckbox.checked
    
    // Find all checkboxes for backdrops in this category
    const backdropRowsInCategory = this.getBackdropsInCategory(categoryId)
    
    // Update all checkboxes in this category
    backdropRowsInCategory.forEach(row => {
      const checkbox = row.querySelector('input[data-photobooth-background-bulk-editor-target="checkbox"]')
      if (checkbox) {
        checkbox.checked = isChecked
      }
    })
    
    // Update the overall state
    this.updateCheckboxes()
    this.updateCategoryCheckboxStates()
  }
  
  getBackdropsInCategory(categoryId) {
    // Find the category header row
    const categoryCheckbox = this.categoryCheckboxTargets.find(cb => cb.dataset.categoryId === categoryId)
    if (!categoryCheckbox) return []
    
    const categoryRow = categoryCheckbox.closest('tr')
    const tbody = categoryRow.parentElement
    
    // Get all rows after the category header until the next category header or end
    const backdropRows = []
    let currentRow = categoryRow.nextElementSibling
    
    while (currentRow && !currentRow.classList.contains('no-drag')) {
      backdropRows.push(currentRow)
      currentRow = currentRow.nextElementSibling
    }
    
    return backdropRows
  }
  
  updateCategoryCheckboxStates() {
    this.categoryCheckboxTargets.forEach(categoryCheckbox => {
      const categoryId = categoryCheckbox.dataset.categoryId
      const backdropsInCategory = this.getBackdropsInCategory(categoryId)
      const checkboxesInCategory = backdropsInCategory.map(row => 
        row.querySelector('input[data-photobooth-background-bulk-editor-target="checkbox"]')
      ).filter(Boolean)
      
      const checkedCount = checkboxesInCategory.filter(cb => cb.checked).length
      
      if (checkedCount === 0) {
        categoryCheckbox.checked = false
        categoryCheckbox.indeterminate = false
      } else if (checkedCount === checkboxesInCategory.length) {
        categoryCheckbox.checked = true
        categoryCheckbox.indeterminate = false
      } else {
        categoryCheckbox.checked = false
        categoryCheckbox.indeterminate = true
      }
    })
  }
}